import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import PropTypes from 'prop-types';
// layouts
import MainLayout from 'src/layouts/main';
import SimpleLayout from 'src/layouts/simple';
import CompactLayout from 'src/layouts/compact';
// components
import { LoadingScreen, SplashScreen } from 'src/components/loading-screen';
import { AuthGuard } from 'src/auth/guard';
import DashboardLayout from 'src/layouts/dashboard';
// hooks
import useBrandName from 'src/hooks/use-brand-name';

// ----------------------------------------------------------------------

export const HomePage = lazy(() => import('src/pages/home'));
export const EuropePage = lazy(() => import('src/pages/europe'));
const Page500 = lazy(() => import('src/pages/500'));
const Page403 = lazy(() => import('src/pages/403'));
const Page404 = lazy(() => import('src/pages/404'));
const FaqsPage = lazy(() => import('src/pages/faqs'));
const AboutPage = lazy(() => import('src/pages/about-us'));
const RefundPolicyPage = lazy(() => import('src/pages/refund-policy'));
const ContactPage = lazy(() => import('src/pages/contact-us'));
const PricingPage = lazy(() => import('src/pages/pricing'));
const PaymentPage = lazy(() => import('src/pages/payment'));
const ComingSoonPage = lazy(() => import('src/pages/coming-soon'));
const PrivacyPolicyPage = lazy(() => import('src/pages/privacy-policy'));
const TermsAndConditionsPage = lazy(() => import('src/pages/terms-and-conditions'));
const MaintenancePage = lazy(() => import('src/pages/maintenance'));
const SettingsPage = lazy(() => import('src/pages/settings'));
// PRODUCT
const ProductListPage = lazy(() => import('src/pages/product/list'));
const ProductDetailsPage = lazy(() => import('src/pages/product/details'));
const ProductCheckoutPage = lazy(() => import('src/pages/product/checkout'));
// COUNTRY
const CountryListPage = lazy(() => import('src/pages/country/list'));
const CountryDetailsPage = lazy(() => import('src/pages/country/details'));
// BLOG
const PostListPage = lazy(() => import('src/pages/post/list'));
const PostDetailsPage = lazy(() => import('src/pages/post/details'));
const BlogPage = lazy(() => import('src/pages/blogs/view/list'));
const BlogDetailsPage = lazy(() => import('src/pages/blogs/view/details'));

// Conditional wrapper component for agent protection
const ConditionalAuthGuard = ({ children }) => {
  const { isAgent } = useBrandName();

  if (isAgent) {
    return <AuthGuard>{children}</AuthGuard>;
  }

  return <>{children}</>;
};

ConditionalAuthGuard.propTypes = {
  children: PropTypes.node,
};

// ----------------------------------------------------------------------

export const mainRoutes = [
  {
    element: (
      <ConditionalAuthGuard>
        <MainLayout>
          <Suspense fallback={<SplashScreen />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ConditionalAuthGuard>
    ),
    children: [
      { path: 'contact-us', element: <ContactPage /> },
      { path: 'about-us', element: <AboutPage /> },
      // { path: 'faqs', element: <FaqsPage /> },
      // {
      //   path: 'product',
      //   children: [
      //     { element: <ProductListPage />, index: true },
      //     { path: 'list', element: <ProductListPage /> },
      //     { path: ':id', element: <ProductDetailsPage /> },
      //     { path: 'checkout', element: <ProductCheckoutPage /> },
      //   ],
      // },
      {
        path: 'apply-visa',
        children: [
          { element: <Page404 />, index: true },
          { path: ':code', element: <CountryDetailsPage /> },
        ],
      },
      {
        path: 'blog',
        children: [
          { element: <BlogPage />, index: true },
          { path: ':code', element: <BlogDetailsPage /> },
        ],
      },
      // {
      //   path: 'post',
      //   children: [
      //     { element: <PostListPage />, index: true },
      //     { path: 'list', element: <PostListPage /> },
      //     { path: ':title', element: <PostDetailsPage /> },
      //   ],
      // },
    ],
  },
  {
    element: (
      <SimpleLayout>
        <Suspense fallback={<SplashScreen />}>
          <Outlet />
        </Suspense>
      </SimpleLayout>
    ),
    children: [
      { path: 'refund-policy', element: <RefundPolicyPage /> },
      { path: 'privacy-policy', element: <PrivacyPolicyPage /> },
      { path: 'terms-and-conditions', element: <TermsAndConditionsPage /> },
    ],
  },
  // {
  //   element: (
  //     <SimpleLayout>
  //       <Suspense fallback={<SplashScreen />}>
  //         <Outlet />
  //       </Suspense>
  //     </SimpleLayout>
  //   ),
  //   children: [
  //     { path: 'pricing', element: <PricingPage /> },
  //     { path: 'payment', element: <PaymentPage /> },
  //   ],
  // },
  {
    element: (
      <AuthGuard>
        <MainLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </AuthGuard>
    ),
    children: [{ path: 'settings', element: <SettingsPage /> }],
  },
  {
    element: (
      <ConditionalAuthGuard>
        <CompactLayout>
          <Suspense fallback={<SplashScreen />}>
            <Outlet />
          </Suspense>
        </CompactLayout>
      </ConditionalAuthGuard>
    ),
    children: [
      { path: 'coming-soon', element: <ComingSoonPage /> },
      { path: 'maintenance', element: <MaintenancePage /> },
      { path: '500', element: <Page500 /> },
      { path: '404', element: <Page404 /> },
      { path: '403', element: <Page403 /> },
    ],
  },
];
